import type { BoardGeometry, Cell, PlayerColor } from '@/shared/board/types'

/**
 * Геометрия доски — порт App\Domain\Ludo\Board.
 *
 * Второй реализации правил в проекте быть не должно, но у игры на одном
 * устройстве нет сервера, который посчитал бы доску: считать некому. Поэтому
 * порт буквальный — те же формулы и тот же порядок обхода, — а равенство
 * с оригиналом держит корпус проверочных партий (см. golden.test.ts).
 *
 * Всё считается формулами от двух чисел, а не таблицами координат: доска
 * собирается из ОДНОЙ четверти поворотами на 90° против часовой стрелки —
 * в ту же сторону, куда идут фишки.
 */

/** На сколько клеток луч выходит за центральный квадрат. */
export const ARM_LENGTH = 9

/** Ширина луча — торец, он же сторона центрального квадрата. */
export const ARM_WIDTH = 7

/** Сторона доски. Углы вне креста заняты дворами. */
export const SIZE = 2 * ARM_LENGTH + ARM_WIDTH

/** Фишек у игрока. Столько же клеток в доме: победа — занять дом целиком. */
export const TOKENS_PER_PLAYER = 5

/** Клеток кольца на одну четверть. */
export const QUARTER = 2 * ARM_LENGTH + ARM_WIDTH - 1

/** Длина общего кольца. */
export const RING_LENGTH = 4 * QUARTER

/** Длина домашней дорожки. */
export const HOME_LENGTH = TOKENS_PER_PLAYER

/** Насколько вход в дом отстоит назад от своей стартовой клетки. */
const START_TO_ENTRY = 2

/** Клеток кольца в круге фишки: от своей стартовой до входа в свой дом. */
export const LAP_LENGTH = RING_LENGTH - START_TO_ENTRY

/** Цвета в порядке обхода доски: от него зависит вся геометрия игрока. */
export const COLORS: PlayerColor[] = ['red', 'green', 'yellow', 'blue']

const INNER_FROM = ARM_LENGTH
const INNER_TO = ARM_LENGTH + ARM_WIDTH - 1
const ARM_WIDTH_HALF = (ARM_WIDTH - 1) / 2
const MIDDLE = ARM_LENGTH + ARM_WIDTH_HALF

/** Номер игрока по порядку обхода доски. */
export function order(color: PlayerColor): number {
  return COLORS.indexOf(color)
}

/** Стартовая клетка игрока — индекс в кольце. */
export function startIndex(color: PlayerColor): number {
  return order(color) * QUARTER
}

/** Клетка кольца, после которой фишка сворачивает в свой дом. */
export function entryIndex(color: PlayerColor): number {
  return (startIndex(color) - START_TO_ENTRY + RING_LENGTH) % RING_LENGTH
}

/** Поворот клетки на 90°·turn против часовой стрелки вокруг центра доски. */
function rotate(cell: Cell, turn: number): Cell {
  let turned = cell

  for (let i = 0; i < turn; i++) {
    turned = { row: SIZE - 1 - turned.col, col: turned.row }
  }

  return turned
}

/** Кольцо в порядке хода — против часовой стрелки, индекс 0 — старт красного. */
export function ring(): Cell[] {
  const quarter: Cell[] = []

  // Клетки торца до угловой: с них начинается четверть, потому что старт
  // стоит через клетку от входа в дом, а не на самом углу.
  for (let col = INNER_FROM + ARM_WIDTH_HALF - START_TO_ENTRY; col > INNER_FROM; col--) {
    quarter.push({ row: 0, col })
  }

  // Вниз по левой стороне верхнего луча — до клетки внутреннего угла.
  for (let row = 0; row <= INNER_FROM; row++) {
    quarter.push({ row, col: INNER_FROM })
  }

  // На запад по верхней стороне левого луча: угловая клетка общая, её уже
  // добавили — в этом вся суть креста.
  for (let col = ARM_LENGTH - 1; col >= 0; col--) {
    quarter.push({ row: INNER_FROM, col })
  }

  // Вниз по торцу левого луча; хвост торца уедет в следующую четверть.
  for (let row = INNER_FROM + 1; row <= INNER_TO - ARM_WIDTH_HALF + START_TO_ENTRY - 1; row++) {
    quarter.push({ row, col: 0 })
  }

  const cells: Cell[] = []

  for (const turn of [0, 1, 2, 3]) {
    for (const cell of quarter) {
      cells.push(rotate(cell, turn))
    }
  }

  return cells
}

/** Клетка принадлежит центральному квадрату. */
function insideCenter(cell: Cell): boolean {
  return (
    cell.row >= INNER_FROM && cell.row <= INNER_TO && cell.col >= INNER_FROM && cell.col <= INNER_TO
  )
}

/** Клетка принадлежит кресту, а не угловому двору. */
function insideCross(cell: Cell): boolean {
  return (
    (cell.col >= INNER_FROM && cell.col <= INNER_TO) ||
    (cell.row >= INNER_FROM && cell.row <= INNER_TO)
  )
}

/**
 * Внутренние углы креста — те, из которых прыгают.
 *
 * Ищутся не по номеру, а по признаку: единственные клетки кольца, попавшие
 * внутрь центрального квадрата. Так список не разъедется с геометрией.
 */
export function innerCorners(): { index: number; cell: Cell }[] {
  return ring()
    .map((cell, index) => ({ index, cell }))
    .filter(({ cell }) => insideCenter(cell))
}

/** Индексы угловых клеток в порядке хода. */
export function cornerIndexes(): number[] {
  return innerCorners().map((corner) => corner.index)
}

/**
 * Места фишек во дворе левого верхнего угла — «пятёркой», как точки на кубике.
 *
 * Рядом из пяти они стояли раньше, и у ряда есть беда: при повороте доски он
 * встаёт то строкой, то столбиком, — двор красного и двор зелёного выглядели
 * по-разному, хотя это одно и то же место. Пятёрка совпадает сама с собой при
 * повороте на 90°, поэтому все четыре двора одинаковы при любом развороте.
 * Так же раскладывает двор меандр (`yardSlots` в `features/board/layout.ts`)
 * и бэкенд (`Board::baseSlots()`).
 *
 * Углы — на соседних клетках от середины, а не вразрядку по двору: фишка
 * стоит на клетке, и лишняя клетка между ними разбивала фигуру на пять
 * отдельных кружков. Двор у креста вдвое шире, чем у меандра, но фигура
 * в обоих одна и та же — она про пять фишек, а не про размер двора.
 *
 * Мест ровно пять, потому что пять и фишек; разойдётся это
 * с TOKENS_PER_PLAYER — поймает проверка в бэкендовом BoardTest.
 */
function baseSlots(): Cell[] {
  const middle = Math.floor((ARM_LENGTH - 1) / 2)
  const step = 1

  return [
    { row: middle - step, col: middle - step },
    { row: middle - step, col: middle + step },
    { row: middle, col: middle },
    { row: middle + step, col: middle - step },
    { row: middle + step, col: middle + step },
  ]
}

/** Поворот прямоугольника: стороны при повороте на 90° меняются местами. */
function rotateRect(
  rect: { row: number; col: number; height: number; width: number },
  turn: number,
): { row: number; col: number; height: number; width: number } {
  const first = rotate({ row: rect.row, col: rect.col }, turn)
  const second = rotate({ row: rect.row + rect.height - 1, col: rect.col + rect.width - 1 }, turn)

  return {
    row: Math.min(first.row, second.row),
    col: Math.min(first.col, second.col),
    height: Math.abs(first.row - second.row) + 1,
    width: Math.abs(first.col - second.col) + 1,
  }
}

/** Игроки: стартовая клетка, вход в дом, домашняя дорожка, двор. */
export function players(): BoardGeometry['players'] {
  const home: Cell[] = Array.from({ length: HOME_LENGTH }, (_, i) => ({
    row: i + 1,
    col: MIDDLE,
  }))
  const slots = baseSlots()

  return COLORS.map((color, turn) => ({
    color,
    startIndex: turn * QUARTER,
    entryIndex: (turn * QUARTER - START_TO_ENTRY + RING_LENGTH) % RING_LENGTH,
    home: home.map((cell) => rotate(cell, turn)),
    base: {
      rect: rotateRect({ row: 0, col: 0, height: ARM_LENGTH, width: ARM_LENGTH }, turn),
      slots: slots.map((cell) => rotate(cell, turn)),
    },
  }))
}

/**
 * Клетки внутри креста, по которым никто не ходит.
 *
 * Считаются вычитанием, а не перечислением: что не кольцо, не дом и не
 * центр — то неигровое.
 */
export function deadCells(): Cell[] {
  const used = new Set<string>()
  const key = (cell: Cell): string => `${cell.row}:${cell.col}`

  for (const cell of ring()) used.add(key(cell))
  for (const player of players()) for (const cell of player.home) used.add(key(cell))

  const dead: Cell[] = []

  for (let row = 0; row < SIZE; row++) {
    for (let col = 0; col < SIZE; col++) {
      const cell = { row, col }

      if (!insideCross(cell) || insideCenter(cell) || used.has(key(cell))) continue

      dead.push(cell)
    }
  }

  return dead
}

/**
 * Полное описание доски — в том же виде, в каком его отдаёт сервер.
 *
 * Так экран партии не знает, откуда доска приехала: из сети или посчитана
 * на месте.
 */
export function geometry(): BoardGeometry {
  return {
    size: SIZE,
    armLength: ARM_LENGTH,
    armWidth: ARM_WIDTH,
    ringLength: RING_LENGTH,
    homeLength: HOME_LENGTH,
    lapLength: LAP_LENGTH,
    tokensPerPlayer: TOKENS_PER_PLAYER,
    ring: ring(),
    players: players(),
    center: {
      rect: { row: INNER_FROM, col: INNER_FROM, height: ARM_WIDTH, width: ARM_WIDTH },
    },
    innerCorners: innerCorners(),
    deadCells: deadCells(),
  }
}
